/**
 * [BLURY] | 🥭 useStudio
 * Single source of truth for the studio: the composition, the output ratio and
 * the editor's own UI state (selection, handle visibility, grain seed).
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { create } from 'zustand';
import { clampWidth, normaliseRatio, outputSize, previewSizeFor } from '@/engine/ratio';
import type {
  Composition,
  ExportFormat,
  GrainBlend,
  GrainType,
  Hex,
  NumericField,
  PaletteField,
  Ratio,
  Shape,
  Size,
} from '@/engine/types';
import { createRandom } from '@/engine/random';
import {
  DEFAULT_EXPORT_QUALITY,
  NEW_SHAPE_COLOR,
  REFERENCE_RATIO,
  REFERENCE_WIDTH,
  referenceComposition,
} from './defaults';

/** Output size, preview size and the scale between them, derived together. */
function resolveSizes(ratio: Ratio, width: number) {
  const size = outputSize(ratio, width);
  const preview = previewSizeFor(size);
  return { size, previewSize: preview.size, previewScale: preview.scale };
}

interface StudioState {
  composition: Composition;
  ratio: Ratio;
  /** Width the export is written at, in pixels. */
  outputWidth: number;
  /** Full output size — what an export is rendered at. */
  size: Size;
  /** What the preview canvas is rendered at; capped for responsiveness. */
  previewSize: Size;
  /** size.width / previewSize.width — 1 when the preview is full size. */
  previewScale: number;
  exportFormat: ExportFormat;
  /** 0–1, lossy formats only. */
  exportQuality: number;
  selected: number;
  showHandles: boolean;
  /** Bumped whenever the grain should be re-rolled. */
  seed: number;

  setNumeric: (field: NumericField, value: number) => void;
  setColor: (field: PaletteField, value: Hex) => void;
  setGrainBlend: (value: GrainBlend) => void;
  setGrainType: (value: GrainType) => void;
  setRatio: (w: number, h: number) => void;
  setOutputWidth: (width: number) => void;
  setExportFormat: (format: ExportFormat) => void;
  setExportQuality: (quality: number) => void;

  addShape: (at?: { x: number; y: number }) => void;
  updateShape: (index: number, patch: Partial<Shape>) => void;
  removeShape: (index: number) => void;
  select: (index: number) => void;

  toggleHandles: () => void;
  shuffle: () => void;
  reset: () => void;
}

const initialComposition = referenceComposition();

export const useStudio = create<StudioState>()((set, get) => ({
  composition: initialComposition,
  ratio: REFERENCE_RATIO,
  outputWidth: REFERENCE_WIDTH,
  ...resolveSizes(REFERENCE_RATIO, REFERENCE_WIDTH),
  exportFormat: 'png',
  exportQuality: DEFAULT_EXPORT_QUALITY,
  selected: -1,
  showHandles: true,
  seed: 1,

  setNumeric: (field, value) =>
    set(state => ({ composition: { ...state.composition, [field]: value } })),

  setColor: (field, value) =>
    set(state => ({ composition: { ...state.composition, [field]: value } })),

  setGrainBlend: value => set(state => ({ composition: { ...state.composition, gblend: value } })),

  setGrainType: value => set(state => ({ composition: { ...state.composition, gtype: value } })),

  setRatio: (w, h) =>
    set(state => {
      const ratio = normaliseRatio(w, h);
      // The width is the user's, so a ratio change moves the height.
      return { ratio, ...resolveSizes(ratio, state.outputWidth) };
    }),

  setOutputWidth: width =>
    set(state => {
      const outputWidth = clampWidth(width);
      return { outputWidth, ...resolveSizes(state.ratio, outputWidth) };
    }),

  setExportFormat: format => set({ exportFormat: format }),

  setExportQuality: quality => set({ exportQuality: quality }),

  addShape: at =>
    set(state => {
      const random = createRandom(state.seed + state.composition.shapes.length);
      const shape: Shape = {
        x: at ? at.x : 0.3 + random() * 0.4,
        y: at ? at.y : 0.2 + random() * 0.4,
        r: at ? 0.24 : 0.22,
        c: NEW_SHAPE_COLOR,
        a: 0.8,
      };
      const shapes = [...state.composition.shapes, shape];
      return {
        composition: { ...state.composition, shapes },
        selected: shapes.length - 1,
      };
    }),

  updateShape: (index, patch) =>
    set(state => {
      const target = state.composition.shapes[index];
      if (!target) {
        console.warn(`updateShape: no shape at index ${index}, skipping`);
        return {};
      }
      const shapes = state.composition.shapes.map((s, i) => (i === index ? { ...s, ...patch } : s));
      return { composition: { ...state.composition, shapes } };
    }),

  removeShape: index =>
    set(state => {
      if (!state.composition.shapes[index]) {
        console.warn(`removeShape: no shape at index ${index}, skipping`);
        return {};
      }
      const shapes = state.composition.shapes.filter((_, i) => i !== index);
      return { composition: { ...state.composition, shapes }, selected: -1 };
    }),

  select: index => set({ selected: index }),

  toggleHandles: () => set(state => ({ showHandles: !state.showHandles })),

  shuffle: () => {
    const seed = (Date.now() % 100000) | 0;
    const random = createRandom(seed);
    const shapes = get().composition.shapes.map(s => ({
      ...s,
      x: -0.1 + random() * 1.2,
      y: -0.1 + random() * 0.9,
      r: 0.16 + random() * 0.32,
    }));
    set(state => ({ composition: { ...state.composition, shapes }, seed }));
  },

  reset: () =>
    set({
      composition: referenceComposition(),
      ratio: REFERENCE_RATIO,
      outputWidth: REFERENCE_WIDTH,
      ...resolveSizes(REFERENCE_RATIO, REFERENCE_WIDTH),
      selected: -1,
      seed: 1,
    }),
}));
